import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, Profile } from '../lib/supabase'
import { useAuth } from '../context/auth'
import { Shield, Package, CreditCard, MapPin, User, Truck, Wrench, Building2, Loader2, Eye, EyeOff, Check, X, ArrowLeft } from 'lucide-react'

type Mode = 'signin' | 'signup'
type Role = Exclude<Profile['role'], 'admin'>

const roles: { value: Role; label: string; icon: typeof User; desc: string }[] = [
  { value: 'customer', label: 'Customer', icon: User, desc: 'Request towing & services' },
  { value: 'tower', label: 'Tower', icon: Truck, desc: 'Tow vehicles & provide roadside services only' },
  { value: 'garage', label: 'Garage', icon: Wrench, desc: 'Repair vehicles & provide mechanical services only' },
  { value: 'parts_vendor', label: 'Parts Shop', icon: Package, desc: 'Sell vehicle parts online only' },
  { value: 'insurer', label: 'Insurer', icon: Building2, desc: 'Review & approve claims' },
]

export default function LoginPage() {
  const { signIn, signUp } = useAuth()
  const navigate = useNavigate()
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<Role>('customer')
  const [selectedRoles, setSelectedRoles] = useState<Role[]>(['customer'])
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const switchMode = (m: Mode) => {
    setMode(m)
    setError('')
    setInfo('')
    setPassword('')
    setConfirmPassword('')
  }

  const checkPasswordStrength = (pwd: string): { strength: 'weak' | 'medium' | 'strong'; message: string } => {
    if (pwd.length < 6) return { strength: 'weak', message: 'Too short (min 6 characters)' }
    if (pwd.length < 8) return { strength: 'weak', message: 'Weak - add more characters' }
    if (!/[A-Z]/.test(pwd)) return { strength: 'weak', message: 'Add uppercase letter' }
    if (!/[a-z]/.test(pwd)) return { strength: 'weak', message: 'Add lowercase letter' }
    if (!/[0-9]/.test(pwd)) return { strength: 'medium', message: 'Add a number' }
    if (!/[^A-Za-z0-9]/.test(pwd)) return { strength: 'medium', message: 'Add special character' }
    return { strength: 'strong', message: 'Strong password!' }
  }

  const passwordStrength = checkPasswordStrength(password)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setInfo('')

    // Password validation for signup
    if (mode === 'signup') {
      if (password !== confirmPassword) {
        setError('Passwords do not match')
        return
      }
      const strength = checkPasswordStrength(password)
      if (strength.strength === 'weak') {
        setError(strength.message)
        return
      }
    }

    setLoading(true)
    setError('')
    setInfo('')

    if (mode === 'signin') {
      console.log('=== SIGN IN FROM LOGIN PAGE ===');
      console.log('Email:', email);
      const { error } = await signIn(email, password)
      console.log('Sign in result:', error);
      if (error) setError(error)
    } else {
      console.log('=== SIGN UP FROM LOGIN PAGE ===');
      console.log('Email:', email);
      console.log('Selected roles:', selectedRoles);
      const result = await signUp(email, password, fullName, selectedRoles)
      console.log('Sign up result:', result);
      
      if (result.error) {
        console.error('Sign up failed:', result.error)
        setError(result.error)
      } else if (result.needsConfirmation) {
        console.log('Email confirmation required')
        setInfo('Account created! Check your email to confirm, then sign in.')
        setMode('signin')
      } else {
        console.log('Registration successful, redirecting...')
        // Registration successful - redirect based on role
        const primaryRole = selectedRoles[0]
        console.log('Primary role:', primaryRole)
        
        // Wait a moment for session to be set
        setTimeout(() => {
          if (primaryRole !== 'customer') {
            // Business role - redirect to onboarding
            console.log('Redirecting to /onboarding')
            navigate('/onboarding')
          } else {
            // Customer - redirect to home
            console.log('Redirecting to /')
            navigate('/')
          }
        }, 500)
      }
    }

    setLoading(false)
  }

  const fillDemo = (em: string) => {
    setEmail(em)
    setPassword('demo1234')
    setMode('signin')
    setError('')
    setInfo('')
  }

  return (
    <div className="login-page">
      <div className="login-window">
        <div className="login-brand">
          <h1>Mkononi</h1>
          <p className="tagline">One breakdown. One connected journey.</p>
          <div className="login-features">
            <span className="login-feature"><MapPin size={12} style={{ color: 'var(--red)' }} /> Live Tracking</span>
            <span className="login-feature"><Shield size={12} style={{ color: 'var(--blue)' }} /> Auto-Claims</span>
            <span className="login-feature"><Package size={12} style={{ color: 'var(--green)' }} /> Verified Parts</span>
            <span className="login-feature"><CreditCard size={12} style={{ color: 'var(--amber)' }} /> M-Pesa</span>
          </div>
        </div>

        <button
          onClick={() => navigate('/')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.875rem',
            color: 'var(--muted)',
            padding: '0.5rem 0',
            marginBottom: '1rem'
          }}
        >
          <ArrowLeft size={16} />
          Back to Home
        </button>

        <div className="auth-toggle">
          <button type="button" className={`auth-toggle-btn ${mode === 'signin' ? 'active' : ''}`} onClick={() => switchMode('signin')}>Sign In</button>
          <button type="button" className={`auth-toggle-btn ${mode === 'signup' ? 'active' : ''}`} onClick={() => switchMode('signup')}>Sign Up</button>
        </div>

        <div className="login-form-container">
          <h2>{mode === 'signin' ? 'Welcome back' : 'Create your account'}</h2>
          <p className="subtitle">{mode === 'signin' ? 'Sign in to your Mkononi account' : 'Join Mkononi in seconds'}</p>

          {error && <div className="login-error">{error}</div>}
          {info && <div className="login-info">{info}</div>}

          <form onSubmit={handleSubmit}>
            {mode === 'signup' && (
              <div className="form-group">
                <label>Full Name</label>
                <input className="form-input" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="John Mwangi" required />
              </div>
            )}
            <div className="form-group">
              <label>Email</label>
              <input className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
            </div>
            <div className="form-group">
              <label>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  style={{ paddingRight: '2.5rem' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--muted)',
                    padding: '0.25rem'
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {password && (
                <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  {passwordStrength.strength === 'strong' && <Check size={12} color="var(--green)" />}
                  {passwordStrength.strength === 'medium' && <X size={12} color="var(--amber)" />}
                  {passwordStrength.strength === 'weak' && <X size={12} color="var(--red)" />}
                  <span style={{
                    color: passwordStrength.strength === 'strong' ? 'var(--green)' :
                           passwordStrength.strength === 'medium' ? 'var(--amber)' : 'var(--red)',
                    fontWeight: 500
                  }}>
                    {passwordStrength.message}
                  </span>
                </div>
              )}
            </div>

            {mode === 'signup' && (
              <div className="form-group">
                <label>Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--muted)',
                      padding: '0.25rem'
                    }}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: 'var(--red)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <X size={12} />
                    <span>Passwords do not match</span>
                  </div>
                )}
                {confirmPassword && password === confirmPassword && (
                  <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: 'var(--green)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Check size={12} />
                    <span>Passwords match</span>
                  </div>
                )}
              </div>
            )}

            {mode === 'signup' && (
              <div className="form-group">
                <label>
                  I am a... {selectedRoles.length > 1 && <span style={{ fontSize: '0.75rem', color: 'var(--blue)', fontWeight: 500 }}>({selectedRoles.length} roles selected)</span>}
                </label>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                  Select one or more roles (e.g., both Tower and Parts Vendor)
                </div>
                <div className="role-grid">
                  {roles.map((r) => {
                    const Icon = r.icon
                    const isSelected = selectedRoles.includes(r.value)
                    return (
                      <button
                        key={r.value}
                        type="button"
                        className={`role-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedRoles(selectedRoles.filter(role => role !== r.value))
                          } else {
                            setSelectedRoles([...selectedRoles, r.value])
                          }
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                          <Icon size={20} style={{ color: isSelected ? 'var(--red)' : '#666' }} />
                          {isSelected && <Check size={16} style={{ color: 'var(--red)' }} />}
                        </div>
                        <span className="role-label">{r.label}</span>
                        <span className="role-desc">{r.desc}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            <button className="btn-primary" type="submit" disabled={loading} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              {loading && <Loader2 size={16} className="spin" />}
              {mode === 'signin' ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          {mode === 'signin' && (
            <div className="login-demo">
              <p>Demo accounts (password: demo1234):</p>
              <div className="demo-emails">
                <div className="demo-item" onClick={() => fillDemo('admin@mkononi.co.ke')}>
                  <span className="demo-email">admin@mkononi.co.ke</span>
                  <span className="demo-desc">Full system control</span>
                </div>
                <div className="demo-item" onClick={() => fillDemo('customer@mkononi.co.ke')}>
                  <span className="demo-email">customer@mkononi.co.ke</span>
                  <span className="demo-desc">Request services & track jobs</span>
                </div>
                <div className="demo-item" onClick={() => fillDemo('tower@mkononi.co.ke')}>
                  <span className="demo-email">tower@mkononi.co.ke</span>
                  <span className="demo-desc">Tow vehicles & accept jobs</span>
                </div>
                <div className="demo-item" onClick={() => fillDemo('garage@mkononi.co.ke')}>
                  <span className="demo-email">garage@mkononi.co.ke</span>
                  <span className="demo-desc">Repair vehicles & manage services</span>
                </div>
                <div className="demo-item" onClick={() => fillDemo('parts@mkononi.co.ke')}>
                  <span className="demo-email">parts@mkononi.co.ke</span>
                  <span className="demo-desc">Sell parts & process orders</span>
                </div>
                <div className="demo-item" onClick={() => fillDemo('insurer@mkononi.co.ke')}>
                  <span className="demo-email">insurer@mkononi.co.ke</span>
                  <span className="demo-desc">Review & approve claims</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
